import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { approveMcpOAuth } from "@/services/api";

// ── MCP SVG icon ──────────────────────────────────────────────────────────────

const McpIcon = () => (
  <svg className="w-6 h-6 text-purple-400" fill="currentColor" fillRule="evenodd"
       viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
    <path d="M15.688 2.343a2.588 2.588 0 00-3.61 0l-9.626 9.44a.863.863 0 01-1.203 0 .823.823 0 010-1.18l9.626-9.44a4.313 4.313 0 016.016 0 4.116 4.116 0 011.204 3.54 4.3 4.3 0 013.609 1.18l.05.05a4.115 4.115 0 010 5.9l-8.706 8.537a.274.274 0 000 .393l1.788 1.754a.823.823 0 010 1.18.863.863 0 01-1.203 0l-1.788-1.753a1.92 1.92 0 010-2.754l8.706-8.538a2.47 2.47 0 000-3.54l-.05-.049a2.588 2.588 0 00-3.607-.003l-7.172 7.034-.002.002-.098.097a.863.863 0 01-1.204 0 .823.823 0 010-1.18l7.273-7.133a2.47 2.47 0 00-.003-3.537z" />
    <path d="M14.485 4.703a.823.823 0 000-1.18.863.863 0 00-1.204 0l-7.119 6.982a4.115 4.115 0 000 5.9 4.314 4.314 0 006.016 0l7.12-6.982a.823.823 0 000-1.18.863.863 0 00-1.204 0l-7.119 6.982a2.588 2.588 0 01-3.61 0 2.47 2.47 0 010-3.54l7.12-6.982z" />
  </svg>
);

// ── Spinner ───────────────────────────────────────────────────────────────────

const Spinner = () => (
  <svg className="animate-spin w-5 h-5 text-purple-400" fill="none" viewBox="0 0 24 24">
    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
    <path className="opacity-75" fill="currentColor"
          d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
  </svg>
);

// ── Page ──────────────────────────────────────────────────────────────────────

export function McpAuthorizePage() {
  const [searchParams] = useSearchParams();
  const [status, setStatus] = useState<"loading" | "error">("loading");
  const [error, setError] = useState("");

  const clientId = searchParams.get("client_id") ?? "";
  const redirectUri = searchParams.get("redirect_uri") ?? "";
  const codeChallenge = searchParams.get("code_challenge") ?? undefined;
  const codeChallengeMethod = searchParams.get("code_challenge_method") ?? undefined;
  const state = searchParams.get("state") ?? undefined;
  const responseType = searchParams.get("response_type") ?? "";

  useEffect(() => {
    if (!clientId || !redirectUri || responseType !== "code") {
      setStatus("error");
      setError("Invalid authorization request — missing required parameters.");
      return;
    }

    approveMcpOAuth({ client_id: clientId, redirect_uri: redirectUri, code_challenge: codeChallenge, code_challenge_method: codeChallengeMethod, state })
      .then(({ code, state: returnedState, redirect_uri }) => {
        const params = new URLSearchParams({ code });
        if (returnedState) params.set("state", returnedState);
        window.location.href = `${redirect_uri}?${params.toString()}`;
      })
      .catch((err) => {
        const detail = err?.response?.data?.detail ?? "Authorization failed. Please try again.";
        setError(detail);
        setStatus("error");
      });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div className="min-h-screen bg-[#0a0a0a] flex items-center justify-center p-4">
      <div className="bg-[#141414] border border-[#2a2a2a] rounded-2xl p-8 w-full max-w-sm text-center">

        {/* Logo */}
        <div className="w-12 h-12 bg-gradient-to-br from-purple-600 to-purple-800 rounded-xl mx-auto mb-5 flex items-center justify-center">
          <McpIcon />
        </div>

        {status === "loading" && (
          <>
            <h1 className="text-white font-semibold text-lg mb-2">Connecting to InstaVault</h1>
            <p className="text-gray-400 text-sm mb-6">Authorizing access to your account…</p>
            <div className="flex justify-center">
              <Spinner />
            </div>
          </>
        )}

        {status === "error" && (
          <>
            <h1 className="text-white font-semibold text-lg mb-2">Authorization Failed</h1>
            <div className="bg-red-950/40 border border-red-900/50 rounded-lg px-4 py-3 mb-4">
              <p className="text-red-400 text-sm">{error}</p>
            </div>
            <p className="text-gray-500 text-xs">Close this window and try connecting again.</p>
          </>
        )}

      </div>
    </div>
  );
}
